import { useState } from "react";
import Swal from "sweetalert2";
import {
  X,
  Calendar,
  FileText,
  AlertCircle,
  DollarSign,
  Paperclip,
  RefreshCw,
  Clock,
  Download,
  Eye,
} from "lucide-react";

export const DocumentDetailDrawer = ({
  document,
  isOpen,
  onClose,
  onRenew,
}) => {
  const [activeTab, setActiveTab] = useState("info");

  if (!isOpen || !document) return null;

  const customData = document.custom_data || {};
  const customFieldsConfig =
    document.document_type?.custom_fields_config ||
    document.document_type?.custom_fields ||
    [];

  const tabs = [
    { id: "info", label: "Info", icon: FileText },
    {
      id: "archivos",
      label: "Archivos",
      icon: Paperclip,
      count: document.attachments?.length || 0,
    },
    { id: "historial", label: "Historial", icon: Clock },
    {
      id: "alertas",
      label: "Alertas",
      icon: AlertCircle,
      count: document.alerts?.length || 0,
    },
  ];

  const renderCustomField = (fieldConfig) => {
    const value = customData[fieldConfig.name];

    if (value === undefined || value === null || value === "") {
      return null;
    }

    let displayValue = value;

    if (fieldConfig.type === "select") {
      const option = fieldConfig.options?.find((opt) => opt.value === value);
      displayValue = option?.label || value;
    } else if (fieldConfig.type === "multiselect") {
      const selectedOptions = Array.isArray(value) ? value : [value];
      displayValue = selectedOptions
        .map((val) => {
          const opt = fieldConfig.options?.find((o) => o.value === val);
          return opt?.label || val;
        })
        .join(", ");
    } else if (fieldConfig.type === "date") {
      displayValue = new Date(value).toLocaleDateString("es-PE");
    } else if (fieldConfig.type === "number") {
      displayValue = parseFloat(value).toLocaleString("es-PE");
    } else if (fieldConfig.type === "checkbox") {
      displayValue = value ? "Sí" : "No";
    }

    return (
      <div key={fieldConfig.name} className="mb-4">
        <span className="block text-sm font-medium text-gray-600 mb-1">
          {fieldConfig.label}
        </span>
        <p className="text-base text-gray-900 font-medium">
          {displayValue || "-"}
        </p>
      </div>
    );
  };

  // ✅ FUNCIÓN PARA VER ARCHIVO (CON AUTENTICACIÓN)
  const handleViewFile = async (attachment) => {
    try {
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_URL}/attachments/${attachment.id}/view`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");

        // Limpiar el objeto URL después de abrirlo
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const error = await response.json();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "No se pudo ver el archivo",
        });
      }
    } catch (error) {
      console.error("❌ Error al ver archivo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar el archivo",
      });
    }
  };

  // ✅ FUNCIÓN PARA DESCARGAR ARCHIVO (CON AUTENTICACIÓN)
  // ✅ FUNCIÓN PARA DESCARGAR ARCHIVO (CON AUTENTICACIÓN) - CORREGIDA
  const handleDownloadFile = async (attachment) => {
    try {
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_URL}/attachments/${attachment.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // ✅ USAR window.document en lugar de document
        const a = window.document.createElement("a");
        a.href = url;
        a.download = attachment.file_name;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const error = await response.json();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "No se pudo descargar el archivo",
        });
      }
    } catch (error) {
      console.error("❌ Error al descargar archivo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo descargar el archivo",
      });
    }
  };
  const handleDeleteAttachment = async (attachmentId, fileName) => {
    const result = await Swal.fire({
      title: "¿Eliminar archivo?",
      html: `¿Estás seguro de eliminar el archivo:<br><strong>${fileName}</strong>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_URL}/documents/${document.id}/attachments/${attachmentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "¡Eliminado!",
          text: "Archivo eliminado correctamente",
          timer: 2000,
          showConfirmButton: false,
        });

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al eliminar");
      }
    } catch (error) {
      console.error("❌ Error eliminando archivo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo eliminar el archivo",
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: "bg-green-100", text: "text-green-800", label: "Vigente" },
      warning: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Por Vencer",
      },
      urgent: {
        bg: "bg-orange-100",
        text: "text-orange-800",
        label: "Urgente",
      },
      expired: { bg: "bg-red-100", text: "text-red-800", label: "Vencido" },
    };
    const badge = badges[status] || badges.active;
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-white mr-3" />
              <div>
                <h2 className="text-xl font-bold text-white">
                  {document.document_type?.name || "Documento"}
                </h2>
                <p className="text-sm text-blue-100">
                  {document.documentable?.placa ||
                    `${document.documentable?.nombre} ${document.documentable?.apellidos}` ||
                    document.documentable?.nombre_comercial ||
                    "Sin información"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onRenew(document)}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                title="Renovar"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="sticky top-[72px] bg-white border-b border-gray-200 z-10">
          <div className="flex px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-6 py-3 font-medium text-sm transition-all ${activeTab === tab.id
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    {tab.count > 0 && (
                      <span
                        className={`ml-1 px-2 py-0.5 text-xs rounded-full ${activeTab === tab.id
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* TAB: INFO */}
          {activeTab === "info" && (
            <div className="space-y-6">
              {/* Estado */}
              <div className="flex items-center justify-between pb-4 border-b">
                <span className="text-sm font-medium text-gray-600">
                  Estado
                </span>
                {getStatusBadge(document.status)}
              </div>

              {/* SECCIÓN 1: INFORMACIÓN GENERAL */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Información General
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">
                      Tipo
                    </span>
                    <p className="text-base text-gray-900 font-medium">
                      {document.document_type?.name || "-"}
                    </p>
                  </div>

                  {document.document_number && (
                    <div>
                      <span className="block text-sm font-medium text-gray-600 mb-1">
                        N° Documento
                      </span>
                      <p className="text-base text-gray-900 font-medium">
                        {document.document_number}
                      </p>
                    </div>
                  )}

                  {document.issuing_entity && (
                    <div>
                      <span className="block text-sm font-medium text-gray-600 mb-1">
                        Entidad Emisora
                      </span>
                      <p className="text-base text-gray-900 font-medium">
                        {document.issuing_entity}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* SECCIÓN 2: CAMPOS PERSONALIZADOS */}
              {customFieldsConfig.length > 0 &&
                Object.keys(customData).length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-indigo-600" />
                      Información Específica
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {customFieldsConfig.map((fieldConfig) =>
                        renderCustomField(fieldConfig),
                      )}
                    </div>
                  </div>
                )}

              {/* SECCIÓN 3: VIGENCIA */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-green-600" />
                  Vigencia
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">
                      Emisión
                    </span>
                    <p className="text-base text-gray-900 font-medium">
                      {document.issue_date
                        ? new Date(document.issue_date).toLocaleDateString(
                          "es-PE",
                        )
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">
                      Vencimiento
                    </span>
                    <p className="text-base text-gray-900 font-medium">
                      {document.expiration_date
                        ? new Date(document.expiration_date).toLocaleDateString(
                          "es-PE",
                        )
                        : "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* SECCIÓN 4: COSTOS */}
              {document.costs && document.costs.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-purple-600" />
                    Información de Costos
                  </h3>
                  <div className="space-y-3">
                    {document.costs.map((cost, index) => (
                      <div key={index} className="bg-purple-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <span className="block text-sm font-medium text-gray-600 mb-1">
                              Monto
                            </span>
                            <p className="text-lg text-gray-900 font-bold">
                              S/. {parseFloat(cost.amount).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <span className="block text-sm font-medium text-gray-600 mb-1">
                              Método de Pago
                            </span>
                            <p className="text-base text-gray-900 font-medium">
                              {cost.payment_method === "cash"
                                ? "💵 Efectivo"
                                : cost.payment_method === "transfer"
                                  ? "🏦 Transferencia"
                                  : cost.payment_method === "card"
                                    ? "💳 Tarjeta"
                                    : cost.payment_method === "check"
                                      ? "📝 Cheque"
                                      : cost.payment_method}
                            </p>
                          </div>
                          <div>
                            <span className="block text-sm font-medium text-gray-600 mb-1">
                              Fecha de Pago
                            </span>
                            <p className="text-base text-gray-900 font-medium">
                              {cost.payment_date
                                ? new Date(
                                  cost.payment_date,
                                ).toLocaleDateString("es-PE")
                                : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECCIÓN 5: NOTAS */}
              {document.notes && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Notas / Observaciones
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {document.notes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: ARCHIVOS */}
          {activeTab === "archivos" && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Paperclip className="h-5 w-5 mr-2 text-gray-600" />
                Archivos Adjuntos
              </h3>
              {document.attachments && document.attachments.length > 0 ? (
                <div className="space-y-2">
                  {document.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                      <div className="flex items-center flex-1">
                        <div className="p-3 bg-blue-100 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {attachment.file_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(attachment.file_size / 1024).toFixed(1)} KB •{" "}
                            {attachment.mime_type}
                          </p>
                        </div>
                      </div>

                      {/* ✅ BOTONES CON AUTENTICACIÓN */}
                      <div className="flex items-center gap-2">
                        {/* Botón VER */}
                        <button
                          onClick={() => handleViewFile(attachment)}
                          className="p-2 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                          title="Ver archivo"
                        >
                          <Eye className="h-5 w-5" />
                        </button>

                        {/* Botón DESCARGAR */}
                        <button
                          onClick={() => handleDownloadFile(attachment)}
                          className="p-2 text-green-600 hover:bg-green-200 rounded-lg transition-colors"
                          title="Descargar"
                        >
                          <Download className="h-5 w-5" />
                        </button>

                        {/* Botón ELIMINAR */}
                        <button
                          onClick={() =>
                            handleDeleteAttachment(
                              attachment.id,
                              attachment.file_name,
                            )
                          }
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Eliminar archivo"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Paperclip className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">
                    No hay archivos adjuntos
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB: HISTORIAL */}
          {activeTab === "historial" && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-gray-600" />
                Historial de Cambios
              </h3>
              <div className="space-y-4">
                <div className="flex items-start p-4 bg-blue-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg mr-4">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Documento creado
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {document.creator?.name || "Sistema"} •{" "}
                      {new Date(document.fecha_creacion).toLocaleString(
                        "es-PE",
                      )}
                    </p>
                  </div>
                </div>

                {document.fecha_actualizacion &&
                  document.fecha_actualizacion !== document.fecha_creacion && (
                    <div className="flex items-start p-4 bg-gray-50 rounded-lg">
                      <div className="p-2 bg-gray-100 rounded-lg mr-4">
                        <RefreshCw className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Última actualización
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {document.updater?.name || "Sistema"} •{" "}
                          {new Date(
                            document.fecha_actualizacion,
                          ).toLocaleString("es-PE")}
                        </p>
                      </div>
                    </div>
                  )}

                <div className="text-center py-8 text-gray-500 text-sm">
                  <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  El historial detallado estará disponible próximamente
                </div>
              </div>
            </div>
          )}

          {/* TAB: ALERTAS */}
          {activeTab === "alertas" && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                Alertas y Notificaciones
              </h3>
              {document.alerts && document.alerts.length > 0 ? (
                <div className="space-y-3">
                  {document.alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-4 rounded-lg border-l-4 ${alert.alert_level === "critical" ||
                          alert.alert_level === "expired"
                          ? "bg-red-50 border-red-500"
                          : alert.alert_level === "warning"
                            ? "bg-yellow-50 border-yellow-500"
                            : alert.alert_level === "info"
                              ? "bg-blue-50 border-blue-500"
                              : "bg-gray-50 border-gray-500"
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${alert.alert_level === "critical" ||
                                  alert.alert_level === "expired"
                                  ? "bg-red-100 text-red-800"
                                  : alert.alert_level === "warning"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                            >
                              {alert.alert_level === "critical"
                                ? "🔴 Crítico"
                                : alert.alert_level === "expired"
                                  ? "⛔ Vencido"
                                  : alert.alert_level === "warning"
                                    ? "⚠️ Advertencia"
                                    : "ℹ️ Información"}
                            </span>
                            {!alert.is_read && (
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                No leída
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {alert.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            📅{" "}
                            {new Date(alert.alert_date).toLocaleDateString(
                              "es-PE",
                            )}
                            {alert.is_sent && ` • ✅ Enviada`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">
                    No hay alertas activas
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    El documento está en buen estado
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <button
            onClick={() => onRenew(document)}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center"
          >
            <RefreshCw className="h-5 w-5 mr-2" />
            Renovar Documento
          </button>
        </div>
      </div>
    </>
  );
};
